import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "../auth";

const IDLE_MS = 15 * 60 * 1000; // 15 minutos sem actividade
const WARNING_MS = 60 * 1000;   // 1 minuto para reagir antes do logout
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];

export default function InactivityGuard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_MS / 1000);
  const warningRef = useRef(false);
  const idleTimerId = useRef(null);
  const countdownIntervalId = useRef(null);

  const endSession = useCallback(async () => {
    clearTimeout(idleTimerId.current);
    clearInterval(countdownIntervalId.current);
    warningRef.current = false;
    setWarning(false);
    try { await logout(); } catch (e) {}
    try { sessionStorage.setItem("sh_login_notice", "inactivity"); } catch (e) {}
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  const startWarning = useCallback(() => {
    warningRef.current = true;
    setWarning(true);
    let remaining = WARNING_MS / 1000;
    setSecondsLeft(remaining);
    countdownIntervalId.current = setInterval(() => {
      remaining -= 1;
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(countdownIntervalId.current);
        endSession();
      }
    }, 1000);
  }, [endSession]);

  const resetIdleTimer = useCallback(() => {
    clearTimeout(idleTimerId.current);
    idleTimerId.current = setTimeout(startWarning, IDLE_MS);
  }, [startWarning]);

  const handleContinue = useCallback(() => {
    clearInterval(countdownIntervalId.current);
    warningRef.current = false;
    setWarning(false);
    resetIdleTimer();
  }, [resetIdleTimer]);

  useEffect(() => {
    if (!user) {
      clearTimeout(idleTimerId.current);
      clearInterval(countdownIntervalId.current);
      return;
    }
    const onActivity = () => {
      // Enquanto o aviso esta visivel, exige clique explicito em "Continuar sessao"
      if (warningRef.current) return;
      resetIdleTimer();
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    resetIdleTimer();
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearTimeout(idleTimerId.current);
      clearInterval(countdownIntervalId.current);
    };
  }, [user, resetIdleTimer]);

  if (!user || !warning) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center" data-testid="inactivity-modal">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert size={20} className="text-amber-500" />
          <h3 className="text-lg font-bold text-navy">Sessao a terminar por inactividade</h3>
        </div>
        <p className="text-sm text-muted2 mb-5">
          Detectamos inactividade prolongada. A sessao vai terminar em{" "}
          <b className="text-navy" data-testid="inactivity-countdown">{secondsLeft}</b> segundos.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={endSession} data-testid="inactivity-logout-now"
            className="text-sm px-4 py-2 rounded-lg border border-tint text-muted2 hover:bg-slate-50">
            Terminar agora
          </button>
          <button onClick={handleContinue} data-testid="inactivity-continue-btn"
            className="text-sm px-4 py-2 rounded-lg bg-blue text-white font-semibold hover:bg-[#5BB8E8]">
            Continuar sessao
          </button>
        </div>
      </div>
    </div>
  );
}
